import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import zeplinColor from 'theme/Color';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import style from 'components/shared/CustomViewTask/CustomViewTask.module.css';
import { LeadsService } from 'service/leads.service';

interface LeadAttachmentsProps {
  files: any[];
  onFileSelect?: (files: File[]) => void;
  onFileDelete?: (index: number) => void;
  leadId?: string;
}

export const LeadAttachments: React.FC<LeadAttachmentsProps> = ({
  files: defaultFiles,
  onFileSelect,
  onFileDelete,
  leadId,
}) => {
  const [files, setFiles] = React.useState<any[]>(defaultFiles || []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && onFileSelect) {
      const selectedFiles = Array.from(event.target.files);
      onFileSelect(selectedFiles);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (file: any) => {
    const name = file instanceof File ? file.name : file?.fileName;
    const extension = name?.split('.').pop()?.toLowerCase();
    return `/assets/${extension}.svg`;
  };

  const getFileName = (file: any) => {
    if (file instanceof File) return file.name;

    return file?.fileName || file?.s3Key || '';
  };

  const getFileSize = (file: any) => {
    const rawSize = file instanceof File ? file.size : file?.fileSize;
    if (!rawSize) return '0MB';
    return `${(rawSize / 10000).toFixed(2)}MB`;
  };

  const getAllFiles = useCallback(async () => {
    if (!leadId) {
      setFiles(defaultFiles || []);
      return;
    }

    try {
      const response = await LeadsService.getAllLeadFiles(leadId);
      const downloadedfiles = response?.data?.files || [];

      const formattedFiles = defaultFiles.map((val: any) => {
        if (val instanceof File) return val;

        if (val?.fileName && val?.s3Key) {
          return {
            fileName: val.fileName,
            fileSize: val.fileSize,
            downloadUrl: val.downloadUrl,
            s3Key: val.s3Key,
          };
        }

        const matchedFile = downloadedfiles.find((file: any) => file.s3Key === val);
        if (matchedFile) {
          return {
            fileName: matchedFile.fileName,
            fileSize: matchedFile.fileSize,
            downloadUrl: matchedFile.downloadUrl,
            s3Key: matchedFile.s3Key,
          };
        }
        return undefined;
      });

      setFiles(formattedFiles.filter((file: any) => file !== undefined));
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  }, [leadId, defaultFiles]);

  useEffect(() => {
    getAllFiles();
  }, [getAllFiles]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '10px', xl: '16px' },
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
          Attachment
        </Typography>

        {onFileSelect && (
          <Box
            sx={{ display: 'flex', gap: '8px', alignItems: 'center', cursor: 'pointer' }}
            onClick={handleFileInputClick}
          >
            <input
              ref={fileInputRef}
              type='file'
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <img
              width={'24px'}
              height={'24px'}
              src={'/assets/upload-blue.svg'}
              alt='upload'
              style={{ objectFit: 'fill' }}
            />
            <Typography variant='buttonMedium' color={zeplinColor.Brand1}>
              Select File
            </Typography>
          </Box>
        )}
      </Box>

      {files?.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '10px', xl: '16px' } }}>
          {files.map((file, index) => {
            const fileName = getFileName(file);
            const fileIcon = getFileIcon(file);
            const fileSize = getFileSize(file);

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                }}
              >
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <img
                    className={style['file-icon']}
                    src={fileIcon}
                    alt='file-type'
                    style={{ objectFit: 'fill' }}
                  />
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                    <LightTooltip
                      title={
                        <Typography variant='body3' color={zeplinColor.Neutral1}>
                          {fileName}
                        </Typography>
                      }
                      placement='bottom-end'
                      style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                    >
                      <Typography variant='responsiveBody2'>
                        {fileName?.slice(0, 40)}
                        {fileName?.length > 40 && '...'}
                      </Typography>
                    </LightTooltip>
                    <Typography variant='responsiveLabel1'>{fileSize}</Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <a
                    href={file?.downloadUrl?.toString() ?? ''}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <img
                      className={style['icon']}
                      src={'/assets/download.svg'}
                      alt='download'
                      style={{
                        objectFit: 'fill',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    />
                  </a>

                  {onFileDelete && (
                    <img
                      className={style['icon']}
                      src={'/assets/delete-blue.svg'}
                      alt='delete'
                      style={{ objectFit: 'fill', cursor: 'pointer' }}
                      onClick={() => onFileDelete(index)}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant='responsiveBody3' color={zeplinColor.Neutral8} sx={{ padding: '12px' }}>
          No attachments
        </Typography>
      )}
    </Box>
  );
};
